import { useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { TableWrap, Empty, th, td, tr } from "@/components/common";

export async function download(url, params, name) {
  try {
    const r = await api.get(url, { params, responseType: "blob" });
    const href = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = href; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(href), 5000);
  } catch (e) { toast.error(errMsg(e)); }
}

export function Stat({ label, value, tone = "", testid, sub }) {
  return (
    <div className="rounded-xl border bg-card p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`amount mt-1 text-xl font-bold ${tone}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function SimpleTable({ cols, rows, testid, empty = "No entries" }) {
  return (
    <>
      <TableWrap>
        <thead><tr>{cols.map((c) => <th key={c[0]} className={th}>{c[0]}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className={tr} data-testid={`${testid}-row-${i}`}>{cols.map((c) => <td key={c[0]} className={`${td} ${c[2] || ""}`}>{c[1](r)}</td>)}</tr>)}</tbody>
      </TableWrap>
      {!rows.length && <Empty testid={`${testid}-empty`} text={empty} />}
    </>
  );
}

const amt = (k) => (r) => inr(r[k], 2);
export const INV_COLS = [
  ["Invoice", (r) => <><div className="font-semibold">{r.invoice_number}</div><div className="text-[10px] text-muted-foreground">LR {r.lr_number}{r.extra_total > 0 ? " · incl. extras" : ""}</div></>],
  ["Date", (r) => fmtDate(r.date)], ["Party", (r) => r.party_name], ["GSTIN", (r) => r.gstin || "-", "font-mono text-xs"],
  ["Place of Supply", (r) => r.pos_name, "text-xs"], ["Rate", (r) => `${r.rate}%`], ["Taxable", amt("taxable"), "amount"],
  ["CGST", amt("cgst"), "amount"], ["SGST", amt("sgst"), "amount"], ["IGST", amt("igst"), "amount"], ["Invoice Value", amt("value"), "amount font-semibold"],
];

const BADGE = { Matched: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", "Amount mismatch": "bg-rose-500/15 text-rose-600", "Missing in 2B": "bg-amber-500/15 text-amber-700 dark:text-amber-400" };
export const ReconBadge = ({ s }) => <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${BADGE[s] || "bg-secondary text-muted-foreground"}`}>{s}</span>;

export function Upload2B({ month, has2b, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const up = async () => {
    if (!file) return toast.error("Select the GSTR-2B JSON / Excel file");
    const fd = new FormData();
    fd.append("month", month);
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/gst/2b/upload", fd);
      toast.success(`${data.count} invoices loaded from GSTR-2B`);
      if (data.warning) toast.warning(data.warning);
      setFile(null);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Remove uploaded GSTR-2B for ${month}?`)) return;
    try { await api.delete("/gst/2b", { params: { month } }); toast.success("GSTR-2B removed"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed p-3" data-testid="upload-2b-box">
      <span className="text-sm font-semibold">GSTR-2B ({month}):</span>
      <Input type="file" accept=".json,.xlsx,.xls,.csv" className="w-64" data-testid="upload-2b-input" onChange={(e) => setFile(e.target.files[0] || null)} />
      <Button size="sm" disabled={busy} data-testid="upload-2b-btn" onClick={up}><Upload className="mr-1 h-4 w-4" />{has2b ? "Re-upload" : "Upload"} 2B</Button>
      {has2b && <Button size="sm" variant="ghost" data-testid="delete-2b-btn" onClick={del}><Trash2 className="mr-1 h-4 w-4 text-rose-600" />Remove</Button>}
      <span className="text-xs text-muted-foreground">GST portal → Returns Dashboard → GSTR-2B → Download JSON / Excel</span>
    </div>
  );
}
